/**
 * 申請回数一覧 - Table section with 新規登録, 編集, 削除 per row.
 * Pattern: same as 文章テンプレート一覧 (DocumentTemplateListSection).
 */

import React, { useMemo } from 'react';
import type { TableProps } from 'antd';
import { AppTable, AppTableActionButton } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import type { RezeptApplicationCountItem } from '@/services/rezeptApplicationCountService';
import type { ReactNode } from 'react';

function formatYearMonth(ym: number): string {
  if (!ym || ym < 200001) return '';
  const y = Math.floor(ym / 100);
  const m = ym % 100;
  return `${y}年${String(m).padStart(2, '0')}月`;
}

interface RezeptApplicationCountListSectionProps {
  list: RezeptApplicationCountItem[];
  onEdit: (record: RezeptApplicationCountItem) => void;
  onDelete: (record: RezeptApplicationCountItem) => void;
  header?: ReactNode;
}

const RezeptApplicationCountListSection: React.FC<RezeptApplicationCountListSectionProps> = ({
  list,
  onEdit,
  onDelete,
  header,
}) => {
  const tableColumns: TableProps<RezeptApplicationCountItem>['columns'] = useMemo(
    () => [
      {
        title: '施術年月',
        dataIndex: 'rezept_yearmonth',
        key: 'rezept_yearmonth',
        width: 300,
        render: (value: number) => formatYearMonth(value),
        sorter: (a, b) => (a.rezept_yearmonth ?? 0) - (b.rezept_yearmonth ?? 0),
      },
      {
        title: '医療費最終申請回数',
        dataIndex: 'rezept_application_count',
        key: 'rezept_application_count',
        sorter: (a, b) => (a.rezept_application_count ?? 0) - (b.rezept_application_count ?? 0),
      },
      {
        title: '助成費最終申請回数',
        dataIndex: 'rezept_related_application_count',
        key: 'rezept_related_application_count',
        sorter: (a, b) =>
          (a.rezept_related_application_count ?? 0) - (b.rezept_related_application_count ?? 0),
      },
      {
        title: '操作',
        key: 'action',
        width: 170,
        align: 'center' as const,
        render: (_, record) => (
          <div className="flex justify-center gap-1">
            <AppTableActionButton
              action="edit"
              onClick={e => {
                e.stopPropagation();
                onEdit(record);
              }}
              title="編集"
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              onClick={e => {
                e.stopPropagation();
                onDelete(record);
              }}
              icon={<i className="fa fa-trash-o" aria-hidden="true" />}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  );

  return (
    <CollapsibleCard title="申請回数一覧">
      {header}

      <div className="relative overflow-x-auto">
        <div className="app-table-last-col-fit">
          <AppTable<RezeptApplicationCountItem>
            columns={tableColumns}
            dataSource={list}
            rowKey="id"
            pagination={false}
            tableLayout="auto"
            loading={false}
            recordCount={list.length}
            customLabelTotal="検索結果"
          />
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default RezeptApplicationCountListSection;
